import { Box, Typography, capitalize } from "@mui/material";
import { ReactElement, useContext } from "react";
import { useTranslation } from "react-i18next";

import { ReservationBrowsingPeriod, Mode } from "models/Mode";
import { Button } from "ui/components/Button";
import { globalStyles } from "ui/globalStyles";
import { COLORS } from "ui/variables";

import { ReservationsContext } from "../Reservations";
import { SearchIcon } from "../search/SearchIcon";

import { useTopControls } from "./useTopControls";


export function TopControls(): ReactElement {
    const { t } = useTranslation();

    const {
        reservationsControl: {
            browsingPeriod,
            mode,
            onModeChange,
        },
    } = useContext(ReservationsContext);

    const { handleModeChange } = useTopControls();

    return (
        <Box sx={{ "@media print": { display: "none" }, position: "absolute", right: 141.6, top: 7.5 }}>
            <Button
                sx={{
                    ...globalStyles.buttonBlack,
                    ...globalStyles.topMenuButton,
                    bgcolor: COLORS[mode === Mode.SEARCHING ? "selected" : "black"],
                    color: COLORS[mode === Mode.SEARCHING ? "black" : "white"],
                    justifyContent: "center",
                    minWidth: 135,
                    mr: 2.7,
                    px: 2,
                    svg: { stroke: COLORS[mode === Mode.SEARCHING ? "black" : "white"], transition: "stroke 200ms cubic-bezier(0.4, 0, 0.2,1) 0ms" },
                }}
                onClick={() => {
                    if (mode === Mode.SEARCHING) {
                        handleModeChange(Mode.RESERVATION_BROWSING);
                    } else {
                        handleModeChange(Mode.SEARCHING);
                    }
                }}
            >
                <Box
                    sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", width: "100%" }}
                >
                    <SearchIcon/>
                    <Typography sx={{ fontSize: 17.5, fontWeight: "bold" }}>
                        { capitalize(t(mode === Mode.SEARCHING ? "common.close" : "modules.reservations.search")) }
                    </Typography>
                </Box>
            </Button>
            {
                [
                    {
                        browsingPeriod: ReservationBrowsingPeriod.MORNING,
                        isActive: [ Mode.RESERVATION_BROWSING, Mode.RESERVATION_CREATION, Mode.RESERVATION_EDIT, Mode.RESERVATION_PREVIEW ].includes(mode) && browsingPeriod === ReservationBrowsingPeriod.MORNING,
                        label: "modules.reservations.browsingPeriod.MORNING",
                    },
                    {
                        browsingPeriod: ReservationBrowsingPeriod.EVENING,
                        isActive: [ Mode.RESERVATION_BROWSING, Mode.RESERVATION_CREATION, Mode.RESERVATION_EDIT, Mode.RESERVATION_PREVIEW ].includes(mode) && browsingPeriod === ReservationBrowsingPeriod.EVENING,
                        label: "modules.reservations.browsingPeriod.EVENING",
                    },
                ].map(({ browsingPeriod, isActive, label }) =>
                    <Button
                        key={browsingPeriod}
                        sx={{ ...globalStyles.topMenuButton, bgcolor: isActive ? COLORS.selected : undefined, mr: 2.7 }}
                        onClick={() => {
                            if (isActive) {
                                handleModeChange(Mode.RESERVATION_BROWSING, ReservationBrowsingPeriod.SELECTED_HOUR);
                            } else {
                                handleModeChange(Mode.RESERVATION_BROWSING, browsingPeriod);
                            }
                        }}
                    >
                        { capitalize(t(label)) }
                    </Button>,
                )
            }
            <Button
                sx={{ ...globalStyles.topMenuButton, bgcolor: mode === Mode.REPORT ? COLORS.selected : undefined, mr: 2.7 }}
                onClick={() => {
                    if (mode === Mode.REPORT) {
                        handleModeChange(Mode.RESERVATION_BROWSING);
                    } else {
                        handleModeChange(Mode.REPORT);
                    }
                }}
            >
                { capitalize(t("modules.reservations.report.title")) }
            </Button>
            <Button
                sx={{ ...globalStyles.topMenuButton, bgcolor: mode === Mode.DAILY_MESSAGE_EDIT ? COLORS.selected : undefined, mr: 2.7 }}
                onClick={() => {
                    if (mode === Mode.DAILY_MESSAGE_EDIT) {
                        handleModeChange(Mode.RESERVATION_BROWSING);
                    } else {
                        handleModeChange(Mode.DAILY_MESSAGE_EDIT);
                    }
                }}
            >
                { capitalize(t("modules.reservations.dailyMessage.title")) }
            </Button>
            <Button
                sx={globalStyles.topMenuButton}
                onClick={() => handleModeChange(Mode.PRINTING)}
            >
                { capitalize(t("modules.reservations.print")) }
            </Button>
        </Box>
    );
}
